
import React, { useMemo, useEffect } from 'react'
import { useDrag, useDragLayer } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { Icon } from '@/components/ui/icon'
import type { CreatedItem, NodeType } from '../../types'

import { ItemTypes } from '@/lib/canvas/item-types'
export { ItemTypes } from '@/lib/canvas/item-types'

interface ContentCardProps {
  item: CreatedItem
  selected?: boolean
  onSelect?: () => void
  onDragOut?: () => void
  children?: React.ReactNode
}

// Custom drag preview layer - disabled (no preview shown)
function CustomDragPreview() {
  return null
}

export function ContentCard({
  item,
  selected = false,
  onSelect,
  onDragOut,
  children,
}: ContentCardProps) {
  const dragType = item.type === 'text'
    ? ItemTypes.CREATED_TEXT
    : item.type === 'image'
    ? ItemTypes.CREATED_IMAGE
    : item.type === 'video'
    ? ItemTypes.CREATED_VIDEO
    : 'created_item'

  const [{ isDragging }, drag, preview] = useDrag({
    type: dragType,
    item: {
      id: item.id,
      type: item.type,
      content: item.type === 'image' && item.metadata?.versions?.lowRes?.url
        ? item.metadata.versions.lowRes.url
        : item.content,
      metadata: {
        ...item.metadata,
        // Use lowRes dimensions when using lowRes URL (extract from width/height, not dimensions object)
        dimensions: item.type === 'image' && item.metadata?.versions?.lowRes?.url
          ? (item.metadata.versions.lowRes.width && item.metadata.versions.lowRes.height
              ? { width: item.metadata.versions.lowRes.width, height: item.metadata.versions.lowRes.height }
              : item.metadata?.dimensions)
          : item.metadata?.dimensions
      },
    },
    end: (draggedItem, monitor) => {
      const dropResult = monitor.getDropResult<{ dropped: boolean; success?: boolean }>()

      // Only remove item if drop was successful
      if (dropResult?.success) {
        // Defer item removal to next microtask to ensure addNode completes first
        // queueMicrotask is faster than setTimeout and spec-compliant
        queueMicrotask(() => {
          queueMicrotask(() => {
            onDragOut?.()
          })
        })
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  // Disable HTML5 default preview - use custom preview instead
  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  // Stable object URL for Blob/File content — created once, revoked on cleanup
  const objectUrl = useMemo(() => {
    if (item.content instanceof File || item.content instanceof Blob) {
      return URL.createObjectURL(item.content)
    }
    return null
  }, [item.content])

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [objectUrl])

  const renderContent = () => {
    switch (item.type) {
      case 'text':
        return (
          <div className="flex h-full items-start p-3">
            <div className="line-clamp-4 text-xs text-foreground">
              {typeof item.content === 'string' ? item.content : 'Created text'}
            </div>
          </div>
        )

      case 'image':
        return (
          <div className="h-full w-full overflow-hidden">
            {typeof item.content === 'string' ? (
              <img
                src={item.content}
                alt="Generated"
                className="h-full w-full object-cover"
              />
            ) : objectUrl ? (
              <img
                src={objectUrl}
                alt="Generated"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Icon name="image" className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
          </div>
        )

      case 'video':
        return (
          <div className="h-full w-full overflow-hidden relative group">
            {typeof item.content === 'string' ? (
              <video
                src={item.content}
                className="h-full w-full object-cover"
                controls
                muted
                playsInline
                preload="metadata"
              />
            ) : objectUrl ? (
              <video
                src={objectUrl}
                className="h-full w-full object-cover"
                controls
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Icon name="video" className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
          </div>
        )

      case '3d':
        return (
          <div className="h-full w-full overflow-hidden">
            <div className="flex h-full items-center justify-center">
              <Icon name="box" className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
        )

      case 'shader':
        return (
          <div className="h-full w-full overflow-hidden bg-muted">
            <div className="flex h-full items-center justify-center">
              <Icon name="sparkles" className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
        )

      default:
        return (
          <div className="h-full w-full overflow-hidden">
            <div className="flex h-full items-center justify-center">
              <Icon name="file" className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
        )
    }
  }

  return (
    <div
      ref={drag as unknown as React.Ref<HTMLDivElement>}
      className={`relative aspect-video cursor-move overflow-hidden ${
        isDragging ? 'opacity-50' : 'opacity-100'
      } ${
        selected
          ? 'bg-card'
          : 'bg-card hover:bg-accent'
      }`}
      onMouseDown={(e) => {
        e.stopPropagation()
      }}
    >
      {/* Inner border when selected */}
      {selected && (
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] ring-2 ring-inset ring-blue-500 dark:ring-blue-400" />
      )}

      <div onClick={onSelect} className="h-full">
        {renderContent()}
      </div>

      {children}
    </div>
  )
}

// Export CustomDragPreview for global use
export { CustomDragPreview }
